import React, { useState, useCallback } from 'react';
import { browserHistory } from 'react-router';
import CarouselSection from 'screens/ContentDetail/components/CarouselSection';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import Icon from 'components/Icon';
import Loader from 'components/Loader';
import { getTabName, getTabDataUrl } from 'screens/SeriesDetail/SeriesDetailHelpers';
import { helperGetApiHeaders, helperConvertApiRoute } from 'helpers/ApiHelper';
import { SERIES_SEASON_TILE } from 'components/TileV2';
import Divider from '../Divider';
import theme from './Seasons.module.scss';

const Seasons = ({
  accessToken,
  breakpoint,
  deviceId,
  fetchNextPanelPage,
  fetchPanelData,
  inline,
  isAnonymous,
  pageType,
  panels,
  seasons,
  seriesType,
}) => {
  const [openDropdown, setOpenDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSlidePaginated, setLastSlidePaginated] = useState(null);

  const dropdownBackDrop = 'dropdown-backdrop';

  const handleOutsideClick = e => {
    if (e.target.id === dropdownBackDrop) {
      onDropdownOpen();
    }
  };

  const backdrop = openDropdown ? (
    <div className={theme.dropdownBackDrop} onClick={handleOutsideClick} id={dropdownBackDrop} />
  ) : null;

  const getSeasonName = (seasons, seriesType) =>
    seasons.map(season => {
      const name = getTabName({ tab: season, seriesType });
      return { ...season, name };
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSeasonSelect = item => {
    setOpenDropdown(false);
    const path = `${window.location.origin}/${item.showCode}/${item.url}`;
    browserHistory.replace(path);
    renderCarousel();
  };

  const onDropdownOpen = useCallback(() => {
    if (openDropdown) {
      setOpenDropdown(false);
    } else {
      setOpenDropdown(true);
    }
  }, [openDropdown]);

  const getSelectedItem = useCallback(
    seasons => {
      const selectedItem = _.find(seasons, season => pageType === season.url);
      return selectedItem || seasons[0];
    },
    [pageType],
  );

  const renderDropdown = useCallback(() => {
    const seasonTabs = getSeasonName(seasons, seriesType);
    const selectedItem = getSelectedItem(seasonTabs);

    const dropdownClass = openDropdown ? theme.active : null;

    if (seasonTabs && seasonTabs.length > 1) {
      return (
        <div id="modal">
          <span className={theme.seasonSelector} onClick={onDropdownOpen}>
            <span>{selectedItem.name}</span>
            {<Icon icon="keyboard-arrow-down" color="white" size="large" viewBox="0 0 10 25" />}
          </span>
          <ul className={classNames(theme.dropdownList, dropdownClass)}>
            {seasonTabs &&
              seasonTabs.map(item => (
                <li key={item.id} className={theme.listItem} onClick={() => onSeasonSelect(item)}>
                  {item.name}
                </li>
              ))}
          </ul>
        </div>
      );
    }
    return seasonTabs && seasonTabs[0].name;
  }, [onDropdownOpen, onSeasonSelect, openDropdown, seasons, seriesType, getSelectedItem]);

  const renderCarousel = () => {
    const panelProps = {
      breakpoint,
      inline,
      seriesType,
    };
    const seasonTabs = getSeasonName(seasons, seriesType);
    const selectedItem = getSelectedItem(seasonTabs);

    const tab = selectedItem;
    const panelUrl = getTabDataUrl(tab);
    const panelId = _.get(tab, '@id', panelUrl);
    const headers = helperGetApiHeaders({ pathKey: pageType, accessToken, deviceId });
    let panelItems;

    if (panels && panels[panelId]) {
      panelItems = panels[panelId].panelData;
    }

    const nextEpisodesUrl = panelItems?.view?.next;
    const shouldPaginate = !!nextEpisodesUrl;
    const shouldFetch = !panelItems && tab;

    const fetchNextPanels = lastSlideIndex => {
      if (shouldPaginate) {
        setIsLoading(true);
        setLastSlidePaginated(lastSlideIndex);
        fetchNextPanelPage(nextEpisodesUrl, headers, panelId).then(() => {
          setIsLoading(false);
        });
      }
    };

    if (shouldFetch) {
      const { '@id': id } = tab;
      const url = panelUrl ? helperConvertApiRoute(panelUrl) : helperConvertApiRoute(id);

      fetchPanelData(url, headers, panelId);
    }

    if (shouldFetch || isLoading) {
      return (
        <>
          <div className={theme.loaderContainer}>
            <Loader />
          </div>
          <Divider />
        </>
      );
    }

    if (panelItems) {
      return (
        <>
          {backdrop}
          <div className={theme.seasonCarousel}>
            <CarouselSection
              dropdownHeadline={renderDropdown()}
              isAnonymous={isAnonymous}
              isPosters={false}
              isSeasons
              tileType={SERIES_SEASON_TILE}
              lastSlidePaginated={lastSlidePaginated}
              onLastSlide={fetchNextPanels}
              panel={panelItems}
              {...panelProps}
            />
          </div>
          <Divider />
        </>
      );
    }

    return null;
  };

  return renderCarousel();
};

Seasons.propTypes = {
  accessToken: PropTypes.string,
  breakpoint: PropTypes.string.isRequired,
  deviceId: PropTypes.string,
  fetchPanelData: PropTypes.func.isRequired,
  fetchNextPanelPage: PropTypes.func.isRequired,
  inline: PropTypes.shape({}),
  isAnonymous: PropTypes.bool,
  pageType: PropTypes.string,
  panels: PropTypes.shape({}).isRequired,
  seasons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  seriesType: PropTypes.string.isRequired,
};

export default React.memo(Seasons);
